"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

export type BuilderDraftLifecycleOptions = {
  autoSavePaused?: boolean;
  backgroundSaveEnabled?: boolean;
  debounceMs?: number;
  dirty: boolean;
  enabled?: boolean;
  onError?: (error: unknown) => void;
  onSave: () => Promise<void> | void;
  revisionKey: string;
  unloadProtectionEnabled?: boolean;
};

export type BuilderDraftLifecycle = {
  flush: () => Promise<void>;
  isSaving: boolean;
  retry: () => Promise<void>;
};

/**
 * Shared draft lifecycle for module-owned builder persistence ports.
 *
 * It deliberately knows nothing about Web or Shop revisions. The host passes
 * a stable key for the current editable snapshot and keeps ownership of API
 * calls, conflict recovery and save-state labels. Background persistence is
 * configurable so explicit-save editors can still share flush/retry
 * coordination and unload protection without scheduling writes.
 */
export function useBuilderDraftLifecycle({
  autoSavePaused = false,
  backgroundSaveEnabled = true,
  debounceMs = 1_400,
  dirty,
  enabled = true,
  onError,
  onSave,
  revisionKey,
  unloadProtectionEnabled = true,
}: BuilderDraftLifecycleOptions): BuilderDraftLifecycle {
  const dirtyRef = useRef(dirty);
  const enabledRef = useRef(enabled);
  const onErrorRef = useRef(onError);
  const onSaveRef = useRef(onSave);
  const revisionKeyRef = useRef(revisionKey);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const queuedRevisionRef = useRef<string | null>(null);
  const savedRevisionRef = useRef<string | null>(null);
  const failedRevisionRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const [isSaving, setIsSaving] = useState(false);

  dirtyRef.current = dirty;
  enabledRef.current = enabled;
  onErrorRef.current = onError;
  onSaveRef.current = onSave;
  revisionKeyRef.current = revisionKey;

  const flush = useCallback(async (): Promise<void> => {
    if (!enabledRef.current || !dirtyRef.current) {
      return;
    }

    const requestedRevision = revisionKeyRef.current;

    if (inFlightRef.current) {
      queuedRevisionRef.current = requestedRevision;
      await inFlightRef.current;

      if (
        dirtyRef.current
        && queuedRevisionRef.current !== null
        && queuedRevisionRef.current !== savedRevisionRef.current
      ) {
        queuedRevisionRef.current = null;
        await flush();
      }

      return;
    }

    if (
      requestedRevision === savedRevisionRef.current
      && requestedRevision !== failedRevisionRef.current
    ) {
      return;
    }

    if (mountedRef.current) {
      setIsSaving(true);
    }

    const savePromise = Promise.resolve().then(() => onSaveRef.current());
    inFlightRef.current = savePromise;

    try {
      await savePromise;
      savedRevisionRef.current = requestedRevision;
      failedRevisionRef.current = null;
    } catch (error) {
      failedRevisionRef.current = requestedRevision;
      onErrorRef.current?.(error);
      throw error;
    } finally {
      inFlightRef.current = null;

      if (mountedRef.current) {
        setIsSaving(false);
      }
    }

    const queuedRevision = queuedRevisionRef.current;
    queuedRevisionRef.current = null;

    if (
      enabledRef.current
      && dirtyRef.current
      && queuedRevision !== null
      && queuedRevision !== savedRevisionRef.current
    ) {
      await flush();
    }
  }, []);

  const retry = useCallback(async (): Promise<void> => {
    savedRevisionRef.current = null;
    failedRevisionRef.current = revisionKeyRef.current;
    await flush();
  }, [flush]);

  useEffect(() => {
    if (
      !backgroundSaveEnabled
      || !enabled
      || !dirty
      || autoSavePaused
      || revisionKey === savedRevisionRef.current
    ) {
      return;
    }

    const timeout = window.setTimeout(() => {
      void flush().catch(() => undefined);
    }, Math.max(0, debounceMs));

    return () => window.clearTimeout(timeout);
  }, [
    debounceMs,
    dirty,
    enabled,
    flush,
    autoSavePaused,
    backgroundSaveEnabled,
    revisionKey,
  ]);

  useEffect(() => {
    if (!unloadProtectionEnabled) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current && !inFlightRef.current) {
        return;
      }

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [unloadProtectionEnabled]);

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  return {
    flush,
    isSaving,
    retry,
  };
}
